import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { getMyAdminStatus } from "@/lib/billing.functions";

export const Route = createFileRoute("/_authed/admin/")({
  head: () => ({
    meta: [
      { title: "Admin: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminPage,
});

type CardDef = {
  to:
    | "/admin/dashboard"
    | "/admin/failures"
    | "/admin/leads"
    | "/admin/emails"
    | "/admin/support"
    | "/admin/suppression"
    | "/admin/directory-contacts"
    | "/admin/ledger"
    | "/admin/users"
    | "/admin/credits"
    | "/admin/referrals";
  title: string;
  blurb: string;
};

const CARDS: CardDef[] = [
  {
    to: "/admin/dashboard",
    title: "Cost & observability",
    blurb:
      "Investor Readiness Report cost trend (30d) and pricing-lookup failures (7d).",
  },
  {
    to: "/admin/users",
    title: "Users",
    blurb:
      "Every account across the platform, with search, credits, teams, and rating-request action.",
  },
  {
    to: "/admin/credits",
    title: "Credits",
    blurb:
      "Grant credits, workflow allowances, and free Investor Readiness Reports. Shows recent grants.",
  },
  {
    to: "/admin/referrals",
    title: "Referrals",
    blurb:
      "Create referral codes and review redemption history. Toggle Active to stop new redemptions without losing payout history.",
  },
  {
    to: "/admin/ledger",
    title: "Credit ledger",
    blurb:
      "Append-only history of every credit balance change. Filter by team and source; reconciliation check flags mismatched rows.",
  },
  {
    to: "/admin/failures",
    title: "Admin email failures",
    blurb: "Full table plus drilldown for failed admin-notification sends.",
  },
  {
    to: "/admin/leads",
    title: "Lead-gen leads",
    blurb: "Investor contacts flagged as leads, recency-filtered.",
  },
  {
    to: "/admin/emails",
    title: "Email log",
    blurb: "Transactional email deliveries, filters by template and status.",
  },
  {
    to: "/admin/support",
    title: "Support tickets",
    blurb:
      "User support requests and replies. Filterable by status and category.",
  },
  {
    to: "/admin/suppression",
    title: "Contact suppression",
    blurb:
      "GDPR/CCPA removal-request list. Filters contact reads and MCP output.",
  },
  {
    to: "/admin/directory-contacts",
    title: "Directory contacts import",
    blurb:
      "Reload the directory-wide contacts table from the source workbook. Shows last import status and total row count.",
  },
];

function AdminPage() {
  return (
    <main className="mx-auto max-w-[960px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/dashboard"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to dashboard
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Admin.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Pick a section.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="block rounded-2xl border border-brand-line bg-brand-paper p-5 no-underline transition-colors hover:bg-muted/40"
          >
            <h2 className="font-serif text-[18px] text-brand-ink">
              {c.title} →
            </h2>
            <p className="mt-1 text-[12.5px] text-brand-muted">{c.blurb}</p>
          </Link>
        ))}
      </div>
    </main>
  );
}
